// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// cues.ts — every sound the ship makes, procedurally.
//
// CUE is an object rather than bare strings on purpose. `sfx.play('scam')` on an unknown name returns
// SILENTLY — no throw, no warning — so a typo ships a game with 200 green tests and no sound. Naming
// every cue here means a typo is a compile error, and tests/game.test.ts additionally greps the
// sources so a raw string literal cannot creep back in.

import type { Patch } from '@ben-gy/game-engine/sound';

export const CUE = {
  tap: 'tap',
  snap: 'snap',
  commit: 'commit',
  nope: 'nope',
  seal: 'seal',
  douse: 'douse',
  vent: 'vent',
  breach: 'breach',
  ignite: 'ignite',
  destroy: 'destroy',
  short: 'short',
  scram: 'scram',
  trip: 'trip',
  win: 'win',
  lose: 'lose',
  beat: 'beat',
  go: 'go',
} as const;

export type CueName = (typeof CUE)[keyof typeof CUE];

export const PATCHES: Record<string, Patch> = {
  // the cable
  tap: { type: 'square', freq: [420, 520], dur: 0.04, gain: 0.14 },
  snap: { type: 'square', freq: [660, 760], dur: 0.035, gain: 0.11 },
  commit: { type: 'triangle', freq: [440, 1320], dur: 0.34, gain: 0.24 },
  nope: { type: 'sawtooth', freq: [200, 120], dur: 0.11, gain: 0.16 },

  // the verbs
  seal: { type: 'square', freq: [180, 90], dur: 0.22, gain: 0.22, noise: true },
  douse: { type: 'sine', freq: [900, 240], dur: 0.28, gain: 0.2, noise: true },
  vent: { type: 'sawtooth', freq: [1400, 300], dur: 0.3, gain: 0.24, noise: true },

  // the ship failing
  breach: { type: 'sine', freq: [260, 70], dur: 0.36, gain: 0.3, noise: true },
  ignite: { type: 'sawtooth', freq: [320, 640], dur: 0.2, gain: 0.18, noise: true },
  destroy: { type: 'sawtooth', freq: [200, 40], dur: 0.6, gain: 0.34, noise: true },
  short: { type: 'square', freq: [1800, 200], dur: 0.12, gain: 0.26, noise: true },
  scram: { type: 'sawtooth', freq: [220, 30], dur: 0.75, gain: 0.36, noise: true },
  trip: { type: 'sine', freq: [140, 34], dur: 0.5, gain: 0.34 },

  // the run
  win: { type: 'triangle', freq: [520, 1180], dur: 0.55, gain: 0.28 },
  lose: { type: 'sawtooth', freq: [380, 90], dur: 0.7, gain: 0.3 },
  beat: { type: 'triangle', freq: [640, 640], dur: 0.09, gain: 0.2 },
  go: { type: 'triangle', freq: [880, 1320], dur: 0.24, gain: 0.26 },
};

/** The four-note arpeggio the surge runs to when a route commits. Semitone offsets, in order. */
export const SURGE_STEPS = [0, 4, 7, 12];
